// reducer.ts
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { Address } from '../@types/Address';
import type { CompanySetupStepsKey } from '../@types/Company';
import { setupStepOrder } from '../@types/Company';
import type { Bodacc, CompanyBail, CoproPerformance } from '../@types/Copro';
import type { AttributionApiResponse, CoproAttributed } from '../@types/CoproAttributed';
import type { FonctionsApiResponse } from '../@types/Fonction';
import type { User } from '../@types/User';
import type { CoproData } from '../components/common/CoproForm/CoproForm.module';
import { showToast } from '../components/common/Notification';
import {
  delAttribution,
  getAttribution,
  getBauxCommerciaux,
  getBauxCommerciauxAEcheance,
  getBauxFromCopro,
  getBauxFromCoproAEcheance,
  getBodacc,
  getBodaccHistory,
  getCopro,
  getCoproPerformance,
  getSalaries,
  setAttribution,
} from '../utils/api';
import { JWTHeader } from '../utils/JWTHeader';
import { setToken } from './AuthReducer';
import type { RootState } from './store';

export interface CoproState {
  performance:CoproPerformance | null;
  bauxCommerciaux: CompanyBail[] | null;
  bauxCommerciauxAEcheance: CompanyBail[] | null;
  bauxCopro: CompanyBail[] | null;
  bauxCoproAEcheance: CompanyBail[] | null;
  bodacc : CompanyBail[] | null;
  bodaccHistory: Bodacc[];
  copros: CoproAttributed[];
  coprosFormData: CoproData[];
  users: User[];
  selectedRows: (number[])[];
  reloadPerf: boolean
  selectedDates: { [key: number]: string | null };
}

const initialState: CoproState = {
  performance: null,
  bauxCommerciaux: null,
  bauxCommerciauxAEcheance: null,
  bauxCopro: null,
  bauxCoproAEcheance: null,
  bodacc: null,
  bodaccHistory: null,
  copros: [],
  coprosFormData: [],
  users: [],
  selectedRows: [[], [], [], [], [], [], []], // one array for each activty setup step
  reloadPerf: false,
  selectedDates: {},
};

export const fetchCopro = createAsyncThunk(
  'copro/copro',
  async(_arg: null, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    if (state.auth.company) {
      const response = await fetch(getCopro(state.auth.company.id, true), JWTHeader(state.auth.token));
      if (response.ok) {
        const data = await response.json();

        // Sort copros so those with MANDANT are last
        const sortedCopros = data.results.sort((a, b) => {
          const aHasMandant = a.company.id !== state.auth.company.id && a.company.subcontractors.some((subcontractor) => subcontractor.id === state.auth.company.id);
          const bHasMandant = b.company.id !== state.auth.company.id && b.company.subcontractors.some((subcontractor) => subcontractor.id === state.auth.company.id);
          return aHasMandant - bHasMandant;
        });

        return { ...data, results: sortedCopros };
      }
      if (response.status === 401) {
        thunkApi.dispatch(setToken(null));
      }
      console.error('Error fetching copros');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      return Promise.reject();
    }
    return Promise.reject();
  },
);
export const fetchCoproAttribution = createAsyncThunk(
  'copro/attribution',
  async(_arg: null, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    if (state.auth.company) {
      const response = await fetch(getAttribution(state.auth.company.id), JWTHeader(state.auth.token));
      if (response.ok) {
        return response.json();
      }
      if (response.status === 401) {
        thunkApi.dispatch(setToken(null));
      }
      console.error('Error fetching copros');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      return Promise.reject();
    }
    return Promise.reject();
  },
);

export const fetchUsers = createAsyncThunk(
  'copro/users',
  async(_arg: null, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    if (state.auth.company) {
      // Fetch salariés (excluding agents commerciaux)
      const response = await fetch(getSalaries(state.auth.company.id), JWTHeader(state.auth.token));
      if (response.ok) {
        return response.json();
      }
      if (response.status === 401) {
        thunkApi.dispatch(setToken(null));
      }
      console.error('Error fetching copros');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      return Promise.reject();
    }
    return Promise.reject();
  },
);

export const fetchPerformance = createAsyncThunk(
  'copro/performance',
  async(_arg: null, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    if (state.auth.company) {
      const response = await fetch(getCoproPerformance(state.auth.company.id), JWTHeader(state.auth.token));
      if (response.ok) {
        return response.json();
      }
      if (response.status === 401) {
        thunkApi.dispatch(setToken(null));
      }
      console.error('Error fetching copros');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      return Promise.reject();
    }
    return Promise.reject();
  },
);

export const fetchBauxCommerciaux = createAsyncThunk(
  'copro/baux/commerciaux',
  async(_arg: null, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    if (state.auth.company) {
      const response = await fetch(getBauxCommerciaux(state.auth.company.id), JWTHeader(state.auth.token));
      if (response.ok) {
        return response.json();
      }
      if (response.status === 401) {
        thunkApi.dispatch(setToken(null));
      }
      console.error('Error fetching copros');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      return Promise.reject();
    }
    return Promise.reject();
  },
);

export const fetchBauxCommerciauxAEcheance = createAsyncThunk(
  'copro/baux/commerciaux/echeance',
  async(_arg: null, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    if (state.auth.company) {
      const response = await fetch(getBauxCommerciauxAEcheance(state.auth.company.id), JWTHeader(state.auth.token));
      if (response.ok) {
        return response.json();
      }
      if (response.status === 401) {
        thunkApi.dispatch(setToken(null));
      }
      console.error('Error fetching copros');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      return Promise.reject();
    }
    return Promise.reject();
  },
);

export const fetchBauxFromCopro = createAsyncThunk(
  'copro/baux/copro',
  async(coproId: number, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    if (state.auth.token) {
      const response = await fetch(getBauxFromCopro(coproId), JWTHeader(state.auth.token));
      if (response.ok) {
        return response.json();
      }
      if (response.status === 401) {
        thunkApi.dispatch(setToken(null));
      }
      console.error('Error fetching baux for copro');
      showToast('error', 'Une erreur est survenue lors du chargement des baux.');
      return Promise.reject();
    }
    return Promise.reject();
  },
);

export const fetchBauxFromCoproAEcheance = createAsyncThunk(
  'copro/baux/copro/echeance',
  async(coproId: number, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    if (state.auth.token) {
      const response = await fetch(getBauxFromCoproAEcheance(coproId), JWTHeader(state.auth.token));
      if (response.ok) {
        return response.json();
      }
      if (response.status === 401) {
        thunkApi.dispatch(setToken(null));
      }
      console.error('Error fetching baux for copro');
      showToast('error', 'Une erreur est survenue lors du chargement des baux à échéance.');
      return Promise.reject();
    }
    return Promise.reject();
  },
);

export const fetchBodacc = createAsyncThunk(
  'copro/bodaccs',
  async(_arg: null, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    if (state.auth.company) {
      const response = await fetch(getBodacc(state.auth.company.id), JWTHeader(state.auth.token));
      if (response.ok) {
        return response.json();
      }
      if (response.status === 401) {
        thunkApi.dispatch(setToken(null));
      }
      console.error('Error fetching copros');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      return Promise.reject();
    }
    return Promise.reject();
  },
);

export const fetchBodaccHistory = createAsyncThunk<Bodacc[], string>(
  'copro/bodaccs/history',
  async(SIRET, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const response = await fetch(getBodaccHistory(SIRET), JWTHeader(state.auth.token));
    if (response.ok) {
      return response.json();
    }
    if (response.status === 401) {
      thunkApi.dispatch(setToken(null));
    }
    console.error('Error fetching copros');
    showToast('error', 'Une erreur est survenue lors du chargement des données.');
    return Promise.reject();
  },
);

export const createAttribution = createAsyncThunk(
  'copro/create/attribution',
  async(args: { coproId: number, role: string, userId: number | null }, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const requestInit = JWTHeader(state.auth.token);
    requestInit.headers['Content-Type'] = 'application/json';
    requestInit.method = 'POST';
    requestInit.body = JSON.stringify({ fonctionType: args.role, user: args.userId, toNotifyForNewDpe: true });
    const response = await fetch(setAttribution(args.coproId), requestInit);
    if (response.ok) {
      return response.json();
    }
    if (response.status === 401) {
      thunkApi.dispatch(setToken(null));
    }
    // Try to parse error message from API response
    let errorMessage = 'Une erreur est survenue lors de la mise à jour des données.';
    try {
      const errorData = await response.json();
      if (errorData && errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // Keep default error message if parsing fails
    }
    console.error('Error creating attribution:', errorMessage);
    showToast('error', errorMessage);
    return Promise.reject(errorMessage);
  },
);

export const deleteAttribution = createAsyncThunk(
  'copro/delete/attribution',
  async(attributionId: number, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const requestInit = JWTHeader(state.auth.token);
    requestInit.headers['Content-Type'] = 'application/json';
    requestInit.method = 'DELETE';
    const response = await fetch(delAttribution(attributionId), requestInit);
    if (response.ok) {
      return Promise.resolve(attributionId);
    }
    if (response.status === 401) {
      thunkApi.dispatch(setToken(null));
    }
    // Try to parse error message from API response
    let errorMessage = 'Une erreur est survenue lors de la suppression de l\'attribution.';
    try {
      const errorData = await response.json();
      if (errorData && errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // Keep default error message if parsing fails
    }
    console.error('Error deleting attribution:', errorMessage);
    showToast('error', errorMessage);
    return Promise.reject(errorMessage);
  },
);

const coproSlice = createSlice({
  name: 'copro',
  initialState,
  reducers: {
    addSelectedRow(state, action: PayloadAction<{ step: CompanySetupStepsKey, row: number }>) {
      const stepIndex = setupStepOrder.indexOf(action.payload.step);
      if (stepIndex !== -1 && state.selectedRows[stepIndex].indexOf(action.payload.row) === -1) {
        state.selectedRows[stepIndex].push(action.payload.row);
      }
    },
    removeSelectedRow(state, action: PayloadAction<{ step: CompanySetupStepsKey, row: number }>) {
      const stepIndex = setupStepOrder.indexOf(action.payload.step);
      if (stepIndex !== -1) {
        const index = state.selectedRows[stepIndex].indexOf(action.payload.row);
        if (index >= 0) {
          state.selectedRows[stepIndex].splice(index, 1);
        }
      }
    },
    setSelectedRow(state, action: PayloadAction<{ step: CompanySetupStepsKey, rows: number[] }>) {
      const stepIndex = setupStepOrder.indexOf(action.payload.step);
      if (stepIndex !== -1) {
        state.selectedRows[stepIndex] = action.payload.rows;
      }
    },
    setReloadPerf(state, action: PayloadAction<boolean>) {
      state.reloadPerf = action.payload;
    },
    resetBodaccHistory(state) {
      state.bodaccHistory = null;
    },
    setSelectedDates(state, action: PayloadAction<{ [key: number]: string | null }>) {
      state.selectedDates = action.payload;
    },
    updateSelectedDate(state, action: PayloadAction<{ coproId: number, updatedDate: string }>) {
      const { coproId, updatedDate } = action.payload;
      state.selectedDates[coproId] = updatedDate;
    },
    setCoprosFormData(state, action: PayloadAction<CoproData[]>) {
      state.coprosFormData = action.payload;
    },
    addCoproFormData(state, action: PayloadAction<CoproData>) {
      state.coprosFormData.push(action.payload);
    },
    updateCoproFormData(state, action: PayloadAction<{ id: number; data: Partial<CoproData> }>) {
      const { id, data } = action.payload;
      const index = state.coprosFormData.findIndex((copro) => copro.id === id);

      if (index !== -1) {
        state.coprosFormData[index] = { ...state.coprosFormData[index], ...data } as CoproData;
      }
    },
    removeCoproFormData(state, action: PayloadAction<{ coproId: number }>) {
      const { coproId } = action.payload;
      const index = state.coprosFormData.findIndex((copro) => copro.id === coproId);

      if (index !== -1) {
        state.coprosFormData.splice(index, 1);
      }
    },
    updateCoproAddress(state, action: PayloadAction<{ coproId: number; addressId: number; address: Address }>) {
      const { coproId, addressId, address } = action.payload;
      const copro = state.coprosFormData.find((c) => c.id === coproId);
      if (copro) {
        const addressIndex = copro.addresses.findIndex((a) => a.id === addressId);
        if (addressIndex !== -1) {
          copro.addresses[addressIndex] = { ...copro.addresses[addressIndex], ...address };
        }
      }
    },
    addCoproAddress(state, action: PayloadAction<{ coproId: number; address: Address }>) {
      const { coproId, address } = action.payload;
      const copro = state.coprosFormData.find((c) => c.id === coproId);
      if (copro) {
        copro.addresses.push(address);
      }
    },
    removeCoproAddress(state, action: PayloadAction<{ coproId: number; addressId: number }>) {
      const { coproId, addressId } = action.payload;
      const copro = state.coprosFormData.find((c) => c.id === coproId);
      if (copro) {
        copro.addresses = copro.addresses.filter((address) => address.id !== addressId);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPerformance.fulfilled, (state, action) => {
      state.performance = action.payload;
    });
    builder.addCase(fetchCopro.fulfilled, (state, action) => {
      state.copros = action.payload.results;
    });
    builder.addCase(fetchUsers.fulfilled, (state, action: PayloadAction<FonctionsApiResponse>) => {
      const userMap: { [key: number]: User } = {};

      action.payload.results.forEach((data) => {
        userMap[data.user.id] = {
          ...data.user,
          fonctions: [],
        };

        userMap[data.user.id].fonctions.push({
          id: data.id,
          type: data.type,
          company: data.company,
        });
      });

      state.users = Object.values(userMap);
    });
    builder.addCase(fetchCoproAttribution.fulfilled, (state, action: PayloadAction<AttributionApiResponse[]>) => {
      state.copros = state.copros.map((copro) => ({
        ...copro,
        attributions: action.payload.filter((attr: AttributionApiResponse) => attr.copro.id === copro.id),
      }));
    });
    builder.addCase(createAttribution.fulfilled, (state, action: PayloadAction<AttributionApiResponse>) => {
      state.copros = state.copros.map((copro: CoproAttributed) => {
        if (copro.id == action.payload.copro.id) {
          const { attributions } = copro;
          attributions.push(action.payload);
          return {
            ...copro,
            attributions,
          };
        }
        return copro;
      });
    });
    builder.addCase(deleteAttribution.fulfilled, (state, action) => {
      state.copros = state.copros.map((copro: CoproAttributed) => {
        const index = copro.attributions.findIndex(((attr) => attr.id === action.payload));
        if (index >= 0) {
          const { attributions } = copro;
          attributions.splice(index, 1);
          return {
            ...copro,
            attributions,
          };
        }
        return copro;
      });
    });
    builder.addCase(fetchBauxCommerciaux.fulfilled, (state, action) => {
      state.bauxCommerciaux = action.payload;
    });
    builder.addCase(fetchBauxCommerciauxAEcheance.fulfilled, (state, action) => {
      state.bauxCommerciauxAEcheance = action.payload;
    });
    builder.addCase(fetchBauxFromCopro.fulfilled, (state, action) => {
      state.bauxCopro = action.payload;
    });
    builder.addCase(fetchBauxFromCoproAEcheance.fulfilled, (state, action) => {
      state.bauxCoproAEcheance = action.payload;
    });
    builder.addCase(fetchBodacc.fulfilled, (state, action) => {
      // parse action.payload.bodacc listeetablissements, acte, listepersonnes, modificationsgenerales, jugement, listeprecedentproprietaire from json to object
      action.payload.forEach((bodacc) => {
        if (bodacc.listeetablissements) {bodacc.listeetablissements = JSON.parse(bodacc.listeetablissements);}
        if (bodacc.acte) {bodacc.acte = JSON.parse(bodacc.acte);}
        if (bodacc.listepersonnes) {bodacc.listepersonnes = JSON.parse(bodacc.listepersonnes);}
        if (bodacc.modificationsgenerales) {bodacc.modificationsgenerales = JSON.parse(bodacc.modificationsgenerales);}
        if (bodacc.jugement) {bodacc.jugement = JSON.parse(bodacc.jugement);}
        if (bodacc.listeprecedentproprietaire) {bodacc.listeprecedentproprietaire = JSON.parse(bodacc.listeprecedentproprietaire);}
      });
      state.bodacc = action.payload;
    });
    builder.addCase(fetchBodaccHistory.fulfilled, (state, action) => {
      // parse action.payload.bodacc listeetablissements, acte, listepersonnes, modificationsgenerales, jugement, listeprecedentproprietaire from json to object
      action.payload.forEach((bodacc) => {
        if (bodacc.listeetablissements) {bodacc.listeetablissements = JSON.parse(bodacc.listeetablissements);}
        if (bodacc.acte) {bodacc.acte = JSON.parse(bodacc.acte);}
        if (bodacc.listepersonnes) {bodacc.listepersonnes = JSON.parse(bodacc.listepersonnes);}
        if (bodacc.modificationsgenerales) {bodacc.modificationsgenerales = JSON.parse(bodacc.modificationsgenerales);}
        if (bodacc.jugement) {bodacc.jugement = JSON.parse(bodacc.jugement);}
        if (bodacc.listeprecedentproprietaire) {bodacc.listeprecedentproprietaire = JSON.parse(bodacc.listeprecedentproprietaire);}
      });
      state.bodaccHistory = action.payload;
    });
  },
});

export const {
  addSelectedRow,
  removeSelectedRow,
  setSelectedRow,
  setReloadPerf,
  resetBodaccHistory,
  setSelectedDates,
  updateSelectedDate,
  setCoprosFormData,
  addCoproFormData,
  updateCoproFormData,
  removeCoproFormData,
  updateCoproAddress,
  addCoproAddress,
  removeCoproAddress,
} = coproSlice.actions;
export default coproSlice.reducer;